import { ExternalLink, FileText, Link2, Paperclip } from 'lucide-react';
import type { CommentAttachment } from '@molt/shared';
import type { ReactElement } from 'react';
import { attachmentDownloadPath, formatAttachmentSize } from '@/lib/attachments';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface CommentAttachmentListProps {
  readonly attachments: readonly CommentAttachment[];
}

export function CommentAttachmentList({
  attachments,
}: CommentAttachmentListProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  if (attachments.length === 0) return null;

  return (
    <ul
      className="mt-2 flex flex-col gap-1.5"
      aria-label={t('cardBack.comments.attachments.listLabel')}
    >
      {attachments.map((attachment) => (
        <li key={attachment.id}>{renderAttachment(attachment)}</li>
      ))}
    </ul>
  );
}

function renderAttachment(attachment: CommentAttachment): ReactElement {
  switch (attachment.kind) {
    case 'file':
      return attachment.attachment === null ? (
        <RemovedAttachmentRow />
      ) : (
        <FileAttachmentRow attachment={attachment.attachment} />
      );
    case 'card_link':
      return <CardLinkAttachmentRow attachment={attachment} />;
    case 'board_link':
      return <BoardLinkAttachmentRow attachment={attachment} />;
  }
}

function FileAttachmentRow({
  attachment,
}: {
  readonly attachment: NonNullable<
    Extract<CommentAttachment, { kind: 'file' }>['attachment']
  >;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const href = attachmentDownloadPath(attachment.id);
  if (isImageAttachment(attachment)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="block overflow-hidden rounded-card border border-border bg-surface-raised hover:border-border-strong"
      >
        <img
          src={href}
          alt={attachment.displayName}
          className="max-h-56 w-full object-contain bg-surface"
        />
        <span className="flex min-w-0 items-center gap-2 px-2 py-1.5 text-sm text-ink">
          <FileText aria-hidden="true" className="size-4 shrink-0 text-ink-muted" />
          <span className="min-w-0 flex-1 truncate">{attachment.displayName}</span>
          {attachment.kind === 'file' ? (
            <span className="shrink-0 text-xs text-ink-muted">
              {formatAttachmentSize(t, attachment.sizeBytes)}
            </span>
          ) : null}
        </span>
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex min-w-0 items-center gap-2 rounded-card border border-border bg-surface-raised px-2 py-1.5 text-sm text-ink hover:border-border-strong"
    >
      <FileText aria-hidden="true" className="size-4 shrink-0 text-ink-muted" />
      <span className="min-w-0 flex-1 truncate">{attachment.displayName}</span>
      {attachment.kind === 'file' ? (
        <span className="shrink-0 text-xs text-ink-muted">
          {formatAttachmentSize(t, attachment.sizeBytes)}
        </span>
      ) : null}
    </a>
  );
}

function isImageAttachment(
  attachment: NonNullable<Extract<CommentAttachment, { kind: 'file' }>['attachment']>,
): boolean {
  if (attachment.kind !== 'file') return false;
  const extension = attachment.displayName.split('.').at(-1)?.toLowerCase();
  return (
    extension !== undefined &&
    ['apng', 'avif', 'bmp', 'gif', 'jpeg', 'jpg', 'png', 'svg', 'webp'].includes(
      extension,
    )
  );
}

function RemovedAttachmentRow(): ReactElement {
  const { t } = useInterfaceLanguage();
  return (
    <div className="flex items-center gap-2 rounded-card border border-border bg-surface-raised px-2 py-1.5 text-sm text-ink-muted">
      <Paperclip aria-hidden="true" className="size-4 shrink-0" />
      <span>{t('cardBack.comments.attachments.fileRemoved')}</span>
    </div>
  );
}

function CardLinkAttachmentRow({
  attachment,
}: {
  readonly attachment: Extract<CommentAttachment, { kind: 'card_link' }>;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const card = attachment.card;
  if (card === null) {
    return (
      <LinkedObjectRow
        icon={<Link2 aria-hidden="true" className="size-4 shrink-0" />}
        title={t('cardBack.comments.attachments.cardUnavailable')}
      />
    );
  }
  if (!card.accessible) {
    return (
      <LinkedObjectRow
        icon={<Link2 aria-hidden="true" className="size-4 shrink-0" />}
        title={t('cardBack.comments.attachments.cardNoAccess')}
      />
    );
  }
  return (
    <LinkedObjectRow
      icon={<ExternalLink aria-hidden="true" className="size-4 shrink-0" />}
      title={card.title}
      meta={
        card.archived
          ? t('cardBack.comments.attachments.cardMetaArchived', {
              board: card.boardTitle,
              list: card.list.title,
            })
          : `${card.boardTitle} / ${card.list.title}`
      }
    />
  );
}

function BoardLinkAttachmentRow({
  attachment,
}: {
  readonly attachment: Extract<CommentAttachment, { kind: 'board_link' }>;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const board = attachment.board;
  if (board === null) {
    return (
      <LinkedObjectRow
        icon={<Link2 aria-hidden="true" className="size-4 shrink-0" />}
        title={t('cardBack.comments.attachments.boardUnavailable')}
      />
    );
  }
  if (!board.accessible) {
    return (
      <LinkedObjectRow
        icon={<Link2 aria-hidden="true" className="size-4 shrink-0" />}
        title={t('cardBack.comments.attachments.boardNoAccess')}
      />
    );
  }
  return (
    <LinkedObjectRow
      icon={<ExternalLink aria-hidden="true" className="size-4 shrink-0" />}
      title={board.title}
      meta={t('cardBack.comments.attachments.boardMeta')}
    />
  );
}

function LinkedObjectRow({
  icon,
  title,
  meta,
}: {
  readonly icon: ReactElement;
  readonly title: string;
  readonly meta?: string | undefined;
}): ReactElement {
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-card border border-border bg-surface-raised px-2 py-1.5 text-sm text-ink">
      <span aria-hidden="true" className="shrink-0 text-ink-muted">
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{title}</span>
      {meta === undefined ? null : (
        <span className="min-w-0 shrink truncate text-xs text-ink-muted">{meta}</span>
      )}
    </div>
  );
}
